import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import AnnouncementMarkdown from '@components/Announcement/AnnouncementMarkdown';
import { SEVERITY_OPTIONS } from '@components/Announcement/severity';
import { useAnnouncement } from '@context/AnnouncementContext';
import {
  activateAnnouncement,
  deactivateAnnouncement,
  getAnnouncement,
  saveAnnouncement,
} from '@services/announcementService';

const MARKDOWN_HELP = 'Formato Markdown: **negrita**, # títulos, - listas, [enlace](https://...)';

const errorMessage = (error, fallback) =>
  error?.response?.data?.detail || error?.message || fallback;

const ComunicadoPage = () => {
  const { refresh } = useAnnouncement();

  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', severity: 'warning' });
  const [republish, setRepublish] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [feedback, setFeedback] = useState(null); // { severity, message }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAnnouncement();
        if (cancelled) return;
        setRecord(data);
        setForm({ title: data.title || '', body: data.body || '', severity: data.severity || 'warning' });
      } catch (error) {
        if (!cancelled) setLoadError(errorMessage(error, 'No se pudo cargar el comunicado.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isDirty = useMemo(() => {
    if (!record) return false;
    return (
      form.title !== (record.title || '')
      || form.body !== (record.body || '')
      || form.severity !== (record.severity || 'warning')
      || republish
    );
  }, [form, record, republish]);

  const isComplete = form.title.trim().length > 0 && form.body.trim().length > 0;

  const applyResult = (data, message) => {
    setRecord(data);
    setForm({ title: data.title || '', body: data.body || '', severity: data.severity || 'warning' });
    setRepublish(false);
    setFeedback({ severity: 'success', message });
    // Refresca el contexto para que el propio banner del admin refleje el estado real.
    refresh();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await saveAnnouncement({ ...form, republish });
      applyResult(data, republish ? 'Comunicado guardado y republicado.' : 'Comunicado guardado.');
    } catch (error) {
      setFeedback({ severity: 'error', message: errorMessage(error, 'No se pudo guardar el comunicado.') });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (event) => {
    const next = event.target.checked;
    setToggling(true);
    try {
      const data = next ? await activateAnnouncement() : await deactivateAnnouncement();
      applyResult(data, next ? 'Comunicado activado: ya es visible para los usuarios.' : 'Comunicado desactivado.');
    } catch (error) {
      setFeedback({ severity: 'error', message: errorMessage(error, 'No se pudo cambiar el estado del comunicado.') });
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );
  }

  return (
    <Fade in timeout={600}>
      <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 }, px: { xs: 0, md: 2 }, maxWidth: '1600px', mx: 'auto' }}>

        {/* ── Header ── */}
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Box sx={{ p: 1.5, borderRadius: 3, background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)', display: 'flex' }}>
            <NotificationsIcon sx={{ color: '#fff', fontSize: '2rem' }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#002642">Comunicado</Typography>
            <Typography variant="body2" color="text.secondary">
              Aviso que verán todos los usuarios al abrir la aplicación
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {/* ── Editor ── */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} color="#002642" mb={2}>Contenido</Typography>
                <Stack spacing={2.5}>
                  <TextField
                    label="Título"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    fullWidth
                    inputProps={{ maxLength: 200 }}
                    helperText={`${form.title.length}/200 · Es lo que se muestra en el banner`}
                  />
                  <TextField
                    select
                    label="Severidad"
                    value={form.severity}
                    onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Define el color del banner y del modal"
                  >
                    {SEVERITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </TextField>
                  <TextField
                    label="Cuerpo"
                    value={form.body}
                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                    fullWidth
                    multiline
                    minRows={14}
                    inputProps={{ maxLength: 20000 }}
                    helperText={MARKDOWN_HELP}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Vista previa ── */}
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} color="#002642" mb={2}>Vista previa</Typography>
                <Typography variant="h6" fontWeight={700} color="#002642" mb={1}>
                  {form.title || 'Sin título'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {form.body.trim()
                  ? <AnnouncementMarkdown>{form.body}</AnnouncementMarkdown>
                  : <Typography variant="body2" color="text.secondary">Escribe el cuerpo para ver la previsualización.</Typography>}
              </CardContent>
            </Card>
          </Grid>

          {/* ── Acciones ── */}
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                  >
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={<Checkbox checked={republish} onChange={(e) => setRepublish(e.target.checked)} />}
                        label="Volver a mostrarlo a quienes ya lo cerraron"
                      />
                      {isDirty && <Chip color="warning" size="small" label="Cambios sin guardar" sx={{ alignSelf: 'flex-start' }} />}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !isDirty || !isComplete}
                      >
                        {saving ? 'Guardando…' : 'Guardar'}
                      </Button>

                      <Tooltip title={isDirty ? 'Guarda los cambios antes de activar' : ''}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(record?.is_active)}
                              onChange={handleToggleActive}
                              disabled={toggling || isDirty}
                              color="success"
                            />
                          )}
                          label={record?.is_active ? 'Comunicado activo' : 'Comunicado inactivo'}
                        />
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Divider />

                  <Typography variant="caption" color="text.secondary">
                    Versión {record?.version}
                    {record?.updated_at && ` · Última actualización ${new Date(record.updated_at).toLocaleString('es-CO')} (hora del servidor)`}
                    {record?.updated_by_username && ` por ${record.updated_by_username}`}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Snackbar
          open={Boolean(feedback)}
          autoHideDuration={5000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {feedback ? (
            <Alert severity={feedback.severity} onClose={() => setFeedback(null)} variant="filled">
              {feedback.message}
            </Alert>
          ) : null}
        </Snackbar>
      </Container>
    </Fade>
  );
};

export default ComunicadoPage;
